import { Injectable, computed, inject, signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { HdBodygraphCenter, HdBodygraphChartPreset, HdBodygraphColorState, HdBodygraphConfig } from '../hd-bodygraph.models';
import { createEmptyHdBodygraphConfig, getHdBodygraphGateColorState } from '../hd-bodygraph-config.utils';
import { HD_BODYGRAPH_GATES, HD_BODYGRAPH_GATE_TO_CENTER } from '../hd-bodygraph-metadata';
import { HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';

/**
 * Soort afwijking tussen de getekende chart en de doel-chart voor één poort.
 *
 * - `missing`: de doel-chart heeft hier een activatie, de speler nog niet.
 * - `extra`: de speler heeft hier een activatie die in de doel-chart ontbreekt.
 * - `wrong-color`: beide actief, maar de kleurstaat (personality/design/both)
 *   komt niet overeen.
 */
export type HdDrawChartDiscrepancyKind = 'missing' | 'extra' | 'wrong-color';

/**
 * Eén concrete afwijking. Bevat de poort en het center zodat het paneel een
 * passende hint kan tonen. Voor een **ontbrekende** activatie verwijst de hint
 * bewust **enkel** naar het center — nooit naar het poortnummer of de zijde
 * (design/personality/both). Voor een poort die de speler **zélf fout** heeft
 * aangezet (te veel of verkeerde kleur) mag de hint wél het poortnummer noemen,
 * zodat de speler eerst zijn eigen fouten kan wegwerken.
 */
export interface HdDrawChartDiscrepancy {
  readonly gate: number;
  readonly center: HdBodygraphCenter;
  readonly kind: HdDrawChartDiscrepancyKind;
}

/**
 * Fout-item voor de "Controleer"-lijst: een poort die de speler zélf heeft
 * geactiveerd maar die fout staat (te veel of verkeerde kleur). Bevat het
 * center-label zodat het paneel de fout kan tonen zonder extra services.
 * Verklapt bewust **niets** over wat nog ontbreekt of wat juist is.
 *
 * `wrongColor` is `true` wanneer de poort op zich wél in de doel-chart hoort
 * (juiste poort) maar de speler de verkeerde kleur (personality/design/both)
 * koos. Het paneel mag dit tonen als "Poort X (Center) → Kleur". Bij `false`
 * gaat het om een poort die in de doel-chart helemaal niet geactiveerd is.
 */
export interface HdDrawChartGateError {
  readonly gate: number;
  readonly centerLabel: string;
  readonly wrongColor: boolean;
}

/** Maximale (perfecte) score: 100 punten, −1 per gebruikt hulpmiddel (hint). */
export const HD_DRAW_CHART_MAX_SCORE = 100;

/** Kostprijs van één "Controleer"-actie die fouten oplevert: −5 punten (ongeacht het aantal fouten). */
export const HD_DRAW_CHART_CHECK_COST = 5;

/** Kostprijs van één "Controleer"-actie zonder fouten (chart nog onvolledig): −1 punt. */
export const HD_DRAW_CHART_EMPTY_CHECK_COST = 1;

/**
 * Spel-service voor **"Teken je chart"** (Draw your chart).
 *
 * Dit is in essentie de gewone edit-modus, maar in spel-context: de speler
 * bouwt een lege chart stap voor stap op door poorten te cyclen (zoals in de
 * standaard editor) terwijl deze service na elke wijziging valideert of de
 * getekende {@link HdBodygraphConfig} exact overeenkomt met de doel-chart.
 *
 * Bij een 100%-match eindigt het spel met een winboodschap. Zolang het spel
 * loopt kan de speler via **Submit** of **Hint** een willekeurige hint
 * opvragen over iets wat nog ontbreekt of fout staat; elke hint kost 1 punt.
 *
 * Bewust uitbreidbaar opgezet (zie `docs/human-design/hd-teken-je-chart-spel.md`).
 */
@Injectable({ providedIn: 'root' })
export class HdDrawChartGameService {
  private readonly gateInfoService = inject(HdGateInfoService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly _isActive = signal(false);
  readonly isActive = this._isActive.asReadonly();

  private readonly _isFinished = signal(false);
  readonly isFinished = this._isFinished.asReadonly();

  private readonly _won = signal(false);
  readonly won = this._won.asReadonly();

  private readonly _targetName = signal<string | null>(null);
  readonly targetName = this._targetName.asReadonly();

  private readonly _hintsUsed = signal(0);
  readonly hintsUsed = this._hintsUsed.asReadonly();

  /** Aantal "Controleer"-acties dat een fout opleverde (elk −5 punten). */
  private readonly _checksUsed = signal(0);
  readonly checksUsed = this._checksUsed.asReadonly();

  /** Aantal "Controleer"-acties zonder fouten (chart nog onvolledig, elk −1 punt). */
  private readonly _emptyChecksUsed = signal(0);
  readonly emptyChecksUsed = this._emptyChecksUsed.asReadonly();

  private readonly _currentHint = signal<string | null>(null);
  readonly currentHint = this._currentHint.asReadonly();

  /**
   * Aantal correct getekende poorten t.o.v. de poorten die in de doel-chart
   * effectief geactiveerd zijn. De voortgangsbalk telt enkel die poorten, niet
   * de 64 totale poorten — zo telt een lege chart als 0% i.p.v. al "bijna vol".
   */
  private readonly _matchedGates = signal(0);
  readonly matchedGates = this._matchedGates.asReadonly();

  /** Aantal poorten dat in de doel-chart geactiveerd is (de noemer van de balk). */
  private readonly _targetGateCount = signal(0);
  readonly targetGateCount = this._targetGateCount.asReadonly();

  /**
   * Foutenlijst van de laatste "Controleer"-actie: enkel poorten die de speler
   * zélf heeft aangezet maar die fout staan. Onthult niets over ontbrekende of
   * correcte poorten. Wordt leeggemaakt zodra de speler de chart wijzigt.
   */
  private readonly _submitErrors = signal<readonly HdDrawChartGateError[]>([]);
  readonly submitErrors = this._submitErrors.asReadonly();

  /**
   * `true` wanneer de laatste "Controleer"-actie **geen fouten** vond terwijl de
   * chart nog niet compleet is: alles wat de speler tot dan toe tekende klopt.
   * Wordt leeggemaakt zodra de speler de chart wijzigt of opnieuw controleert
   * (met fouten).
   */
  private readonly _checkAllCorrect = signal(false);
  readonly checkAllCorrect = this._checkAllCorrect.asReadonly();

  /** Totaal aantal poorten (64), gebruikt voor de volledige win-check. */
  readonly totalGates = HD_BODYGRAPH_GATES.length;

  /**
   * Score: start op {@link HD_DRAW_CHART_MAX_SCORE}, −1 per hint,
   * −{@link HD_DRAW_CHART_CHECK_COST} per "Controleer" mét fouten en
   * −{@link HD_DRAW_CHART_EMPTY_CHECK_COST} per "Controleer" zonder fouten,
   * nooit < 0.
   */
  readonly score = computed(() =>
    Math.max(
      0,
      HD_DRAW_CHART_MAX_SCORE
        - this._hintsUsed()
        - this._checksUsed() * HD_DRAW_CHART_CHECK_COST
        - this._emptyChecksUsed() * HD_DRAW_CHART_EMPTY_CHECK_COST,
    ),
  );

  /** Perfecte partij = gewonnen zonder één hulpmiddel (geen hint, geen controle). */
  readonly isPerfectGame = computed(
    () => this._won() && this._hintsUsed() === 0 && this._checksUsed() === 0 && this._emptyChecksUsed() === 0,
  );

  /** Voortgang als afgerond percentage (0–100) t.o.v. de te tekenen poorten. */
  readonly matchPercentage = computed(() => {
    const total = this._targetGateCount();
    if (total === 0) {
      return 0;
    }
    return Math.round((this._matchedGates() / total) * 100);
  });

  readonly maxPossibleScore = HD_DRAW_CHART_MAX_SCORE;

  private targetConfig: HdBodygraphConfig | null = null;
  private targetPreset: HdBodygraphChartPreset | null = null;
  private lastUserConfig: HdBodygraphConfig = createEmptyHdBodygraphConfig();

  /**
   * Start het spel met de gekozen doel-chart. De chart in de editor wordt
   * elders leeggemaakt; deze service houdt enkel de doel-config bij plus de
   * validatie-/hint-state.
   */
  start(targetPreset: HdBodygraphChartPreset | null | undefined): void {
    this.targetPreset = targetPreset ?? null;
    this.targetConfig = targetPreset?.configuration ?? null;
    this._targetName.set(targetPreset?.name ?? null);
    this.resetRoundState();
    this._targetGateCount.set(this.countActiveGates(this.targetConfig));
    this._isActive.set(true);
  }

  stop(): void {
    this._isActive.set(false);
    this._isFinished.set(false);
    this._won.set(false);
    this._currentHint.set(null);
    this._hintsUsed.set(0);
    this._checksUsed.set(0);
    this._emptyChecksUsed.set(0);
    this._matchedGates.set(0);
    this._targetGateCount.set(0);
    this._submitErrors.set([]);
    this._checkAllCorrect.set(false);
    this.lastUserConfig = createEmptyHdBodygraphConfig();
  }

  /** Herstart met dezelfde doel-chart (gebruikt door de "Opnieuw"-knop). */
  restart(): void {
    if (this.targetPreset == null) {
      return;
    }
    this.start(this.targetPreset);
  }

  private resetRoundState(): void {
    this._isFinished.set(false);
    this._won.set(false);
    this._currentHint.set(null);
    this._hintsUsed.set(0);
    this._checksUsed.set(0);
    this._emptyChecksUsed.set(0);
    this._matchedGates.set(0);
    this._submitErrors.set([]);
    this._checkAllCorrect.set(false);
    this.lastUserConfig = createEmptyHdBodygraphConfig();
  }

  /**
   * Valideer de huidige getekende config tegen de doel-chart. Geeft `true`
   * terug op het moment dat de speler **net** wint (alle 64 poorten kloppen),
   * zodat de caller een win-geluid kan afspelen. Wordt na elke wijziging in
   * de chart aangeroepen.
   */
  validateAgainstConfig(userConfig: HdBodygraphConfig): boolean {
    if (!this._isActive() || this.targetConfig == null) {
      return false;
    }

    this.lastUserConfig = userConfig;
    // Een wijziging maakt de vorige "Controleer"-uitkomst achterhaald.
    this._submitErrors.set([]);
    this._checkAllCorrect.set(false);
    this._matchedGates.set(this.countMatchingTargetGates(userConfig, this.targetConfig));

    if (this._isFinished()) {
      return false;
    }

    if (this.countMatchingGates(userConfig, this.targetConfig) === this.totalGates) {
      this._won.set(true);
      this._isFinished.set(true);
      this._currentHint.set(null);
      return true;
    }

    return false;
  }

  /**
   * Door de speler ingedrukte **Controleer**: wanneer de chart perfect is valt
   * dit terug op een gewone win-validatie (returnt `true`, zonder kost). Is de
   * chart nog niet klaar, dan tonen we de **foute aangezette poorten** in een
   * lijstje. De kost hangt af van het resultaat:
   * - zijn er **fouten** → {@link HD_DRAW_CHART_CHECK_COST} punten (ongeacht het
   *   aantal fouten);
   * - zijn er **geen fouten** (enkel nog ontbrekende activaties) →
   *   {@link HD_DRAW_CHART_EMPTY_CHECK_COST} punt.
   *
   * Géén hint en géén info over wat nog ontbreekt of wat juist is; hints lopen
   * uitsluitend via {@link requestHint}.
   */
  submit(): boolean {
    if (!this._isActive() || this._isFinished()) {
      return false;
    }
    if (this.countMatchingGates(this.lastUserConfig, this.targetConfig!) === this.totalGates) {
      return this.validateAgainstConfig(this.lastUserConfig);
    }
    const errors = this.collectUserGateErrors(this.lastUserConfig);
    this._submitErrors.set(errors);
    if (errors.length > 0) {
      this._checkAllCorrect.set(false);
      this._checksUsed.update(count => count + 1);
    } else {
      this._checkAllCorrect.set(true);
      this._emptyChecksUsed.update(count => count + 1);
    }
    return false;
  }

  /** Expliciete hint-knop: toont een willekeurige hint (kost 1 punt). */
  requestHint(): void {
    if (!this._isActive() || this._isFinished()) {
      return;
    }
    this.revealRandomHint();
  }

  private revealRandomHint(): void {
    const discrepancies = this.collectDiscrepancies(this.lastUserConfig);
    if (discrepancies.length === 0) {
      return;
    }
    // Werk eerst de eigen fout aangezette poorten weg (te veel of verkeerde
    // kleur) vóór er hints komen over nog ontbrekende activaties. Zo lang er
    // dergelijke fouten zijn, wijst de hint die aan (met poortnummer).
    const ownErrors = discrepancies.filter(d => d.kind === 'extra' || d.kind === 'wrong-color');
    const pool = ownErrors.length > 0 ? ownErrors : discrepancies;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    this._hintsUsed.update(count => count + 1);
    this._currentHint.set(this.buildHintText(pick));
  }

  private buildHintText(discrepancy: HdDrawChartDiscrepancy): string {
    const lang = this.contentLang.effectiveLang();
    const center = this.gateInfoService.centerLabelIn(discrepancy.center, lang);
    switch (discrepancy.kind) {
      case 'missing':
        return this.transloco.translate('humanDesign.games.drawChart.hintMissing', { center }, lang);
      case 'extra':
        return this.transloco.translate('humanDesign.games.drawChart.hintExtra', { gate: discrepancy.gate }, lang);
      case 'wrong-color':
        return this.transloco.translate('humanDesign.games.drawChart.hintWrongColor', { gate: discrepancy.gate }, lang);
    }
  }

  private collectDiscrepancies(userConfig: HdBodygraphConfig): readonly HdDrawChartDiscrepancy[] {
    if (this.targetConfig == null) {
      return [];
    }
    const result: HdDrawChartDiscrepancy[] = [];
    for (const gate of HD_BODYGRAPH_GATES) {
      const userState = getHdBodygraphGateColorState(userConfig, gate);
      const targetState = getHdBodygraphGateColorState(this.targetConfig, gate);
      const kind = this.classify(userState, targetState);
      if (kind != null) {
        result.push({ gate, center: HD_BODYGRAPH_GATE_TO_CENTER[gate], kind });
      }
    }
    return result;
  }

  private classify(
    userState: HdBodygraphColorState,
    targetState: HdBodygraphColorState,
  ): HdDrawChartDiscrepancyKind | null {
    if (userState === targetState) {
      return null;
    }
    if (targetState !== 'inactive' && userState === 'inactive') {
      return 'missing';
    }
    if (userState !== 'inactive' && targetState === 'inactive') {
      return 'extra';
    }
    return 'wrong-color';
  }

  private countMatchingGates(userConfig: HdBodygraphConfig, targetConfig: HdBodygraphConfig): number {
    let matched = 0;
    for (const gate of HD_BODYGRAPH_GATES) {
      if (getHdBodygraphGateColorState(userConfig, gate) === getHdBodygraphGateColorState(targetConfig, gate)) {
        matched++;
      }
    }
    return matched;
  }

  /**
   * Telt poorten die in de doel-chart geactiveerd zijn én door de speler
   * correct (juiste kleur) getekend zijn. Vormt de teller van de voortgangsbalk.
   */
  private countMatchingTargetGates(userConfig: HdBodygraphConfig, targetConfig: HdBodygraphConfig): number {
    let matched = 0;
    for (const gate of HD_BODYGRAPH_GATES) {
      const targetState = getHdBodygraphGateColorState(targetConfig, gate);
      if (targetState === 'inactive') {
        continue;
      }
      if (getHdBodygraphGateColorState(userConfig, gate) === targetState) {
        matched++;
      }
    }
    return matched;
  }

  /** Telt het aantal geactiveerde poorten in een config (noemer van de balk). */
  private countActiveGates(config: HdBodygraphConfig | null): number {
    if (config == null) {
      return 0;
    }
    let count = 0;
    for (const gate of HD_BODYGRAPH_GATES) {
      if (getHdBodygraphGateColorState(config, gate) !== 'inactive') {
        count++;
      }
    }
    return count;
  }

  /**
   * Verzamelt enkel de poorten die de speler zélf heeft aangezet maar die fout
   * staan (te veel, of verkeerde kleur). Onthult niets over ontbrekende of
   * correcte poorten — enkel de eigen foute klikken.
   */
  private collectUserGateErrors(userConfig: HdBodygraphConfig): readonly HdDrawChartGateError[] {
    if (this.targetConfig == null) {
      return [];
    }
    const lang = this.contentLang.effectiveLang();
    const errors: HdDrawChartGateError[] = [];
    for (const gate of HD_BODYGRAPH_GATES) {
      const userState = getHdBodygraphGateColorState(userConfig, gate);
      if (userState === 'inactive') {
        continue;
      }
      const targetState = getHdBodygraphGateColorState(this.targetConfig, gate);
      if (userState !== targetState) {
        errors.push({
          gate,
          centerLabel: this.gateInfoService.centerLabelIn(HD_BODYGRAPH_GATE_TO_CENTER[gate], lang),
          wrongColor: targetState !== 'inactive',
        });
      }
    }
    return errors;
  }
}
